import { Inject, Injectable, Optional, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, pluck } from 'rxjs/operators';
import { IHeaderLoading, IHeaderMenuItem } from '../header/IHeaderState';
import { ILocaleNameLookup, LocaleAbbreviation } from '../locale-select/ILocaleNameLookup';
import { IOrganization } from '../models/organization/IOrganization';
import { IPractice } from '../models/practice/IPractice';
import { IAuthenticatedUser } from '../models/user-interfaces';
import { BackgroundClassList, ILayoutOptions, ILayoutVisibilitySettings } from './ILayoutOptions';

/** @ignore the internal state of the service. This is initially updated with any configuration provided via the module. */
let state: BehaviorSubject<ILayoutOptions>;

/** @ignore The default values for the object's state. */
const defaults: ILayoutOptions = {
    backgroundClassList: [ 'layout', 'columns', 'responsive', 'bg-fill-white', 'bg-tools', 'background-tools' ],
    username: undefined,
    user: undefined,
    visibility: {
        sidebarButton: false,
        selectPractice: false,
        selectOrganization: false,
        user: false,
        translate: false,
        logo: true,
        whiteLabelLogo: false,
        locale: false,
        header: true,
        footer: true
    },
    practice: undefined,
    practices: [],
    organization: undefined,
    organizations: [],
    value: {
        organization: undefined,
        practice: undefined,
        locale: 'en-US'
    },
    userMenuItems: [
        { name: 'Account!', url: 'https://orthofi.com/Account/MyAccount' },
        { name: 'Log Out', url: 'https://keymaster.orthofi.com/api/account/logout' }
    ],
    loading: {
        practices: false,
        organizations: false
    }
};

/** A service for passing configuration and data to a layout, even requiring creation of a component. This service requires a partial for initialization. */
@Injectable()
export class FoundationLayoutService {
    /** @ignore Creates an instance of FoundationLayoutService. */
    constructor (private _route: ActivatedRoute, @Optional() @Inject('LAYOUT_OPTIONS') private _layoutOptions?: ILayoutOptions) {
        _layoutOptions = _layoutOptions || ({} as ILayoutOptions); // FIXME: LAYOUT_OPTIONS should be a partial, but... https://github.com/ng-packagr/ng-packagr/issues/1185
        // Initialize the state using the options provided to the service's forRoot.
        // TODO: Deep merge.
        const merged: Partial<ILayoutOptions> = {};
        if (_layoutOptions.loading) {
            merged.loading = { ...defaults.loading, ..._layoutOptions.loading };
        }
        if (_layoutOptions.visibility) {
            merged.visibility = { ...defaults.visibility, ..._layoutOptions.visibility };
        }

        state = new BehaviorSubject({ ...defaults, ..._layoutOptions, ...merged });
    }

    /** Experimental: A subject that emits details for any organization selected in the header. Requires practice select visibility. */
    selections = new Subject();

    /** Provide information about the user that is logged in. */
    set user (user: IAuthenticatedUser) {
        state.next({ ...state.value, user });
        // If there's no organization, take the one from the user.
        if (!state.value.organization && state.value.user && state.value.user.OrganizationId && state.value.user.OrganizationName) {
            state.next({
                ...state.value,
                organization: {
                    OrganizationId: state.value.user.OrganizationId,
                    Name: state.value.user.OrganizationName
                }
            });
        }
    }

    /** Update the authenticated user's name in the header, overriding the one retrieved from the user object.  */
    set username (username: string) {
        state.next({ ...state.value, username });
    }

    /** Update the currently selected practice selection. This changes the practice select and practice logo.  */
    set organization (organization: IOrganization) {
        state.next({ ...state.value, organization });
    }

    /** Provide a new list of practices to be displayed in the autocomplete. */
    set organizations (organizations: Array<IOrganization>) {
        state.next({ ...state.value, organizations });
    }

    /** Update the currently selected practice selection. This changes the practice select and practice logo.  */
    set practice (practice: IPractice) {
        state.next({ ...state.value, practice });
    }

    /** Provide a new list of practices to be displayed in the autocomplete. */
    set practices (practices: Array<IPractice>) {
        state.next({ ...state.value, practices });
    }

    /** Provide a new list of classes to apply to the background of the layout body. */
    set backgroundClassList (backgroundClassList: BackgroundClassList) {
        state.next({ ...state.value, backgroundClassList });
    }

    /** Update the locale selection in the header. */
    set locale (locale: LocaleAbbreviation) {
        state.next({ ...state.value, locale });
    }

    /** Update the list of locales displayed in the locale select potion of the header */
    set locales (locales: ILocaleNameLookup) {
        state.next({ ...state.value, locales });
    }

    /**
	 * Override the list of of links used to populate the anchors in the User menu. The default links are set to use production URLs.
	 * @param userMenuItems An array containing a list of urls and presentation text for the menu items.
	 */
    set userMenuItems (userMenuItems: Array<IHeaderMenuItem>) {
        state.next({ ...state.value, userMenuItems });
    }

    /** update which portions of the page layout will appear in the UI.  */
    set visibility (visibility: Partial<ILayoutVisibilitySettings>) {
        const updatedSettings: ILayoutVisibilitySettings = { ...state.value.visibility, ...visibility };
        state.next({ ...state.value, visibility: updatedSettings });
    }

    /** Provide a new list of practices to be displayed in the autocomplete. */
    set loading (loading: Partial<IHeaderLoading>) {
        const updatedSettings: IHeaderLoading = { ...state.value.loading, ...loading };
        state.next({ ...state.value, loading: updatedSettings });
    }

    /** Used by the form to update the selected practice value. Use `practice` to set the value. */
    set practiceValue (practice: IPractice) {
        const values = { ...state.value.value, practice };
        state.next({ ...state.value, value: values });
    }

    /** Used by the form to update the selected organization value. Use `organization` to set the value. */
    set organizationValue (organization: IOrganization) {
        const values = { ...state.value.value, organization };
        state.next({ ...state.value, value: values });
    }

    /** A reference to the behavior subject containing the layout configuration. */
    get state (): BehaviorSubject<ILayoutOptions> {
        return state;
    }

    /** An observable containing the layout's current configuration details. */
    get state$ (): Observable<ILayoutOptions> {
        return state.asObservable();
    }

    /** An observable containing the practice that is currently selected in the dropdown. This will initially emit any pre-populated selection. */
    get practiceSelection$ (): Observable<IPractice> {
        return this.state$.pipe(pluck('value'), pluck('practice'), distinctUntilChanged());
    }

    /** Experimental: An observable containing the organization that is currently selected in the dropdown. This will initially emit any pre-populated selection. */
    get organizationSelection$ (): Observable<IOrganization> {
        return this.state$.pipe(pluck('value'), pluck('organization'), distinctUntilChanged());
    }

    /** Experimental: An observable containing the organization that is currently selected in the dropdown. This will initially emit any pre-populated selection. */
    get localeSelection$ (): Observable<string> {
        return this.state$.pipe(pluck('value'), pluck('locale'), distinctUntilChanged());
    }

    // TODO: Bring in automatic practice selection on URL param change? Would need a reference to the HTTP functions.
    // useUrlParams () {
    // We need to so a
    //   this._route.paramMap.subscribe(params => {
    //     this.prePID = params.get("animal")
    //   });
    // }
}
